export const SITE_URL = "https://rigcalc.pro";

export const TOOLS = [
  {
    slug: "kill-mud-weight-calculator",
    route: "/pressure-control",
    name: "Kill Mud Weight Calculator",
    short: "Kill mud weight, ICP, FCP & MAASP from shut-in data.",
    metaTitle: "Kill Mud Weight Calculator (KMW, ICP, FCP) | RIGCALC",
    metaDescription: "Kill mud weight formula with worked example. Calculate KMW, initial and final circulating pressure and MAASP from SIDPP, mud weight and TVD.",
    intro: "After a kick is shut in, the first number the crew needs is the mud weight that will balance formation pressure. The kill mud weight calculator takes your SIDPP, current mud weight and true vertical depth and gives you KMW, initial circulating pressure (ICP), final circulating pressure (FCP) and MAASP at the shoe.",
    faqs: [
      { q: "Should I add a safety margin to kill mud weight?", a: "Many operators add a trip margin of 0.1–0.3 ppg, but this raises shoe pressure. RIGCALC lets you enter an optional safety factor so you can see its effect before committing." },
      { q: "Why use TVD and not measured depth?", a: "Hydrostatic pressure depends only on vertical height of the fluid column, so all well-control weight calculations use TVD." },
      { q: "What is 0.052?", a: "It converts ppg to a pressure gradient in psi/ft (1 ppg × 0.052 = 0.052 psi/ft)." },
    ],
  },
  {
    slug: "kill-sheet-calculator",
    route: "/kill-sheet",
    name: "Kill Sheet Calculator",
    short: "Full Driller's / Wait & Weight kill sheet with pressure schedule.",
    metaTitle: "Kill Sheet Calculator — Wait & Weight Pressure Schedule | RIGCALC",
    metaDescription: "Build a complete well control kill sheet: KMW, ICP, FCP, strokes surface-to-bit and the drill pipe pressure step-down schedule. Exportable as PDF.",
    intro: "A kill sheet ties every well-control number together: kill weight, circulating pressures, string and annular volumes, strokes and a step-down schedule so the choke operator knows exactly what drill pipe pressure to hold as kill mud goes down the string.",
    faqs: [
      { q: "Driller's method or Wait & Weight?", a: "The schedule here is for Wait & Weight (one circulation). For the Driller's method, hold ICP constant on the first circulation, then follow the schedule with kill mud on the second." },
      { q: "Can I print it for the doghouse?", a: "Yes — Pro users can export the kill sheet as a PDF report." },
    ],
  },
  {
    slug: "kick-tolerance-calculator",
    route: "/kick-tolerance",
    name: "Kick Tolerance Calculator",
    short: "Max kick height & volume before breaking down the shoe.",
    metaTitle: "Kick Tolerance Calculator — Max Kick Volume | RIGCALC",
    metaDescription: "Calculate kick tolerance in bbl and maximum kick height from shoe fracture gradient, mud weight, kick intensity and annular capacity, with GO / NO-GO verdict.",
    intro: "Kick tolerance is the largest influx you can take and still circulate out without fracturing the casing shoe. It is one of the key go/no-go checks before drilling ahead into a new section.",
    faqs: [
      { q: "What kick intensity should I use?", a: "Use your company standard — commonly 0.5 ppg for development wells and 1.0 ppg for exploration." },
      { q: "Does this account for gas expansion?", a: "This is the standard single-bubble model at the shoe. For HPHT or very deep wells, run a full kick simulation." },
    ],
  },
  {
    slug: "tubing-movement-calculator",
    route: "/tubing-movement",
    name: "Tubing Movement Calculator",
    short: "Hooke's law stretch, piston, ballooning and thermal effects.",
    metaTitle: "Tubing Movement Calculator — Hooke's Law, Ballooning, Thermal | RIGCALC",
    metaDescription: "Calculate tubing length change from Hooke's law, piston effect, ballooning and temperature. Formulas and worked examples for completion engineers.",
    intro: "Tubing grows and shrinks when you change pressure, temperature or load. Get it wrong and you unseat packers or part seals. This calculator combines the four classic Lubinski effects so you can space out correctly.",
    faqs: [
      { q: "Do I need consistent units?", a: "Yes. RIGCALC supports both Field (API) and Metric units and converts internally." },
      { q: "Is buckling included?", a: "Helical buckling is not included in this quick tool; check against your completion software for long free strings." },
    ],
  },
  {
    slug: "dogleg-severity-calculator",
    route: "/directional",
    name: "Dogleg Severity & Minimum Curvature Calculator",
    short: "DLS, dogleg angle, ΔTVD, ΔN and ΔE between survey stations.",
    metaTitle: "Dogleg Severity Calculator & Minimum Curvature | RIGCALC",
    metaDescription: "Calculate dogleg severity (°/100 ft) and minimum curvature survey positions between two stations from inclination, azimuth and course length.",
    intro: "Directional drillers and MWD hands run these numbers on every survey. Enter two stations to get dogleg angle, dogleg severity and minimum curvature ΔTVD, northing and easting.",
    faqs: [
      { q: "Why minimum curvature?", a: "It is the industry-standard survey method (API / ISCWSA) because it models the wellbore as a smooth arc between stations." },
    ],
  },
  {
    slug: "torque-and-drag-calculator",
    route: "/torque-drag",
    name: "Torque & Drag Calculator",
    short: "Soft-string hookload for POOH, RIH and rotating, plus torque.",
    metaTitle: "Torque and Drag Calculator — Hookload Prediction | RIGCALC",
    metaDescription: "Soft-string torque and drag model: predict pick-up, slack-off and rotating hookloads and surface torque by section with buoyancy and friction factors.",
    intro: "Predicting pick-up and slack-off weights lets you spot hole problems early and stay within rig and pipe limits. RIGCALC uses a section-by-section soft-string model with buoyancy and friction.",
    faqs: [
      { q: "What friction factor should I use?", a: "Typical starting values: 0.20–0.25 cased hole, 0.25–0.35 open hole with WBM, lower with OBM. Calibrate against actual pick-up/slack-off." },
    ],
  },
  {
    slug: "free-point-calculator",
    route: "/free-point",
    name: "Free Point Calculator",
    short: "Depth of stuck point from pipe stretch and pull.",
    metaTitle: "Free Point Calculator — Stuck Pipe Stretch Formula | RIGCALC",
    metaDescription: "Estimate the free point (stuck pipe depth) from measured stretch, differential pull and pipe weight using the standard 735,294 constant.",
    intro: "When pipe is stuck, a stretch test gives a quick estimate of where it is free before you call wireline. Pull two loads, measure the stretch between them and let the calculator find the free point.",
    faqs: [
      { q: "How accurate is a stretch test?", a: "Usually within 10–15%. Hole friction and mixed strings reduce accuracy; confirm with a free-point tool before backing off." },
    ],
  },
  {
    slug: "fishing-calculator",
    route: "/fishing",
    name: "Fishing Calculator (Pipe Yield, Safe Pull, Jar Impact)",
    short: "Pipe body yield, safe overpull and jar impact force.",
    metaTitle: "Fishing Calculator — Pipe Body Yield & Safe Pull Limit | RIGCALC",
    metaDescription: "Calculate pipe body yield strength, maximum safe overpull and jar impact energy for fishing and stuck pipe operations.",
    intro: "Before jarring or pulling on a fish, know how much you can safely pull. The fishing tools calculate pipe body yield, usable overpull after string weight and design factor, and jar impact.",
    faqs: [
      { q: "What design factor should I use?", a: "Many operators limit to 80–90% of yield for new pipe and lower for premium/class 2 pipe." },
    ],
  },
  {
    slug: "casing-burst-collapse-calculator",
    route: "/collapse-burst",
    name: "Casing Burst & Collapse Calculator",
    short: "Barlow burst and yield collapse ratings.",
    metaTitle: "Casing Burst Pressure Calculator (Barlow) & Collapse | RIGCALC",
    metaDescription: "Calculate casing and tubing burst pressure with Barlow's formula and yield-strength collapse. Worked example for 9-5/8\" 47# N-80.",
    intro: "Check burst and collapse ratings for any OD, wall thickness and grade — handy when you're on location without the spec book.",
    faqs: [
      { q: "Why 0.875?", a: "API allows 12.5% under-tolerance on wall thickness, so burst is calculated on 87.5% of nominal wall." },
    ],
  },
  {
    slug: "hydrostatic-pressure-calculator",
    route: "/hydrostatic",
    name: "Hydrostatic Pressure Calculator",
    short: "Hydrostatic pressure from mud weight and TVD.",
    metaTitle: "Hydrostatic Pressure Calculator — 0.052 × MW × TVD | RIGCALC",
    metaDescription: "Calculate hydrostatic pressure of a mud column in psi or bar from mud weight and true vertical depth. Field and metric units.",
    intro: "Hydrostatic pressure is the base of every well-control calculation. Enter mud weight and TVD to get bottom-hole hydrostatic pressure and gradient.",
    faqs: [{ q: "Does hole angle matter?", a: "No — only TVD matters. Use measured depth only for volume calculations." }],
  },
  {
    slug: "pipe-annular-capacity-calculator",
    route: "/capacities",
    name: "Pipe & Annular Capacity Calculator",
    short: "bbl/ft capacities, volumes and pump strokes.",
    metaTitle: "Annular Capacity Calculator — bbl/ft & Strokes | RIGCALC",
    metaDescription: "Calculate pipe capacity, annular capacity (bbl/ft), volumes and pump strokes to displace. Uses the 1029.4 constant with worked examples.",
    intro: "Capacities drive bottoms-up times, kill sheets, cement jobs and trip sheets. Get pipe and annular capacities, volumes and strokes in seconds.",
    faqs: [{ q: "Where does 1029.4 come from?", a: "It converts inches² × ft to barrels: 1 bbl = 9,702 in³ ÷ (π/4 × 12)." }],
  },
  {
    slug: "mud-weight-up-barite-calculator",
    route: "/mud-weight",
    name: "Mud Weight-Up & Dilution Calculator",
    short: "Barite sacks to weight up, water to weight down.",
    metaTitle: "Barite Calculator — Mud Weight Up & Dilution | RIGCALC",
    metaDescription: "Calculate sacks of barite to weight up a mud system and the volume of water or base fluid needed to weight down. Formulas and example.",
    intro: "Weighting up for a kill or cutting back after a section — the mud weight tools give barite sacks, new volume and dilution volume at a glance.",
    faqs: [{ q: "Does this include volume increase?", a: "Yes — the calculator also reports the new system volume after adding barite." }],
  },
  {
    slug: "cement-slurry-volume-calculator",
    route: "/cementing",
    name: "Cement Slurry Volume & Sacks Calculator",
    short: "Slurry volume, sacks, mix water and displacement.",
    metaTitle: "Cement Calculator — Slurry Volume, Sacks & Displacement | RIGCALC",
    metaDescription: "Calculate cement slurry volume, number of sacks from yield, mix water and displacement volume for a casing cement job.",
    intro: "Plan your cement job: annular slurry volume with excess, shoe track, sacks from slurry yield, mix water and displacement to bump the plug.",
    faqs: [{ q: "How much excess should I add?", a: "Common practice is 10–30% in gauge hole and up to 100% in washed-out open hole — use caliper data where available." }],
  },
  {
    slug: "casing-design-calculator",
    route: "/casing-wizard",
    name: "Casing Design Wizard",
    short: "Pick casing that meets burst, collapse and tension.",
    metaTitle: "Casing Design Calculator — Burst, Collapse, Tension | RIGCALC",
    metaDescription: "Step-by-step casing design wizard: select casing size, weight and grade that meet burst, collapse and tension design factors from a built-in API spec library.",
    intro: "The casing wizard walks you through loads and design factors, then filters the API spec library to casing that passes burst, collapse and tension.",
    faqs: [{ q: "Which design factors does it use?", a: "Common defaults are pre-filled; change them to match your company standard." }],
  },
];

export const toolBySlug = (slug) => TOOLS.find((t) => t.slug === slug);
